import {DomainKeys, Peer, State} from '../../state/types/state.js';
import {GatewayRequest, GwRequestId} from '../../state/types/pending-requests.js';
import {HandleReturnType} from '../../domain.js';
import * as activityState from './state.js'
import {activityThrows, setActivity} from './state.js'
import * as contextState from './../../common/context/state.js'
import {addContext, contextById, newVersion, toCtx} from '../../common/context/state.js'
import {castDraft, Draft, Immutable, produce} from 'immer';
import {byId, byIdInDomainThrows, byIdThrows, isPeerVisibleIdentity, peersInDomain, setPeer} from '../../state/peers.js';
import {Activity, activityDomain, ActivityPeer, ActivityType} from '../../state/types/activity.js';
import {PeerId, Source} from '../../state/types/common.js';
import {reason, Reason, requestToReason, throwReason} from '../../reason.js';
import {getLogger} from '../../logger.js';
import {CommandType, MessageType, SendMessageAction} from '../../node.js';
import {removeGatewayRequest, threadStateAndActions} from '../../state/core.js';
import {
    activityCreated,
    activityInitiated,
    activityJoined,
    activityLeft,
    activityTypesAdded,
    activityTypesRemoved,
    disposePeer,
    error,
    joinedActivity,
    ownerChanged,
    success
} from './messages.js';
import {
    activityIsChild,
    activityMissingActivityType,
    activityNotAuthorized,
    activityOwnerCreationFailed,
    activityRegistrationFailure,
    reasonActivityDestroyed
} from './constants.js';
import {
    CreateActivityRequest,
    DestroyRequest,
    JoinActivityRequest,
    SubscribeRequest,
    UnsubscribeRequest
} from './types/requests.js';
import {createActivityPeer} from './factories.js';
import {Context} from '../../state/types/context.js';
import {activityId, contextId} from '../../common/ids.js';
import {parse} from '../../restrictions.js';

const log = getLogger('gateway.domains.activity.activities');

export function requestToActivity(state: State, request: CreateActivityRequest, peer: Peer): [State, Activity, Context] {
    const [idsAfterContext, ctxId] = contextId(state.ids);
    const rp = parse(request.read_permissions);
    const wp = parse(request.write_permissions);
    const context = toCtx(peer, ctxId, request.initial_context, 'activity', rp, wp, ctxId, newVersion());
    const [idsAfterActivity, actId] = activityId(idsAfterContext);
    const activity = {id: actId, type: request.activity_type, contextId: ctxId, initiator: request.peer_id};

    activity['client_request'] = request;
    return [{...state, ids: idsAfterActivity}, activity, context];

}


/**
 * Handles the case where a joining peer should be part of an activity.

 - joins the peer to the context
 - associates the peer with the activity and sets as owner if needed

 */
export function activityMemberJoined(state: State, activityId: string | undefined, peer: Peer, gwRequest: GatewayRequest): State {
    const activity = activityState.activity(state, activityId);
    if (activity) {
        const peerId = peer.id;
        const isOwner = gwRequest.activity?.['owner?'];
        const context = contextState.contextById(state, activity.contextId);
        state = activityState.setActivity(state, activity.id, produce(activity, (draft: Draft<Activity>) => {
            if (!draft.participants) {
                draft.participants = new Set<PeerId>();
            }
            draft.participants.add(peerId)
            if (isOwner) {
                draft.owner = peer.id;
            }
            if (draft.gatewayRequests) {
                draft.gatewayRequests.delete(gwRequest.id);
                if (draft.gatewayRequests.size === 0) {
                    delete draft.gatewayRequests;
                }
            }
        }));
        state = setPeer(state, peerId, produce(peer, (draft: Draft<Peer>) => {
            draft[DomainKeys.Activity]!.member = activity.id;
            draft[DomainKeys.Activity]!.reload = gwRequest.reload;
        }));
        state = contextState.addContextMember(state, context, peerId);
    }
    return state;
}

function broadcastAddTypes(state: State, user: string, types: ActivityType[]): SendMessageAction<'types-added'>[] {
    return peersInDomain(state, activityDomain)
        .filter(peer => peer.identity.user === user)
        .map(peer => activityTypesAdded(peer.source, peer.id, types));
}

function broadcastRemoveTypes(state: State, user: string, types: string[]): SendMessageAction<'types-removed'>[] {
    return peersInDomain(state, activityDomain)
        .filter(peer => peer.identity.user === user)
        .map(peer => activityTypesRemoved(peer.source, peer.id, types));
}

export function addTypes(state: State, source: Source, requestId: string, peerId: PeerId, types: ActivityType[]): HandleReturnType {
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const user = peer.identity.user;
    if (user) {
        let messages: SendMessageAction<MessageType>[] = broadcastAddTypes(state, user, types);
        messages = messages.concat(success(source, requestId, peerId));
        return [activityState.addActivityTypes(state, user, types), messages];
    }
    return [state, [error(source, requestId, peerId, reason(activityRegistrationFailure, `Registering peer is missing an user in its identity ${JSON.stringify(peer.identity)}`))]];
}

export function removeTypes(state: State, source:Source, request): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, types} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const user = peer.identity.user;
    if (user) {
        const typeNames = new Set<string>(types);
        const actualTypes = Array.from<string>(new Set<string>(Object.keys(activityState.activityTypes(state, user)!).filter(typeName => typeNames.has(typeName))));
        let messages: SendMessageAction<MessageType>[] = broadcastRemoveTypes(state, user, actualTypes);
        messages = messages.concat(success(source, requestId, peerId))
        return [activityState.removeActivityTypes(state, user, actualTypes), messages];
    } else {
        return [state, [error(source, requestId, peerId, reason(activityRegistrationFailure, `Removing peer is missing an user in its identity ${JSON.stringify(peer.identity)}`))]];
    }
}

export function announceTypes(state: State, peer: Peer): SendMessageAction<'types-added'>[] {

    const activityTypes = activityState.activityTypes(state, peer.identity['user']);
    if (activityTypes) {
        // todo distinct??
        const userTypes = Object.values<ActivityType>(activityTypes);
        return [activityTypesAdded(peer.source, peer.id, userTypes)];
    }
    return [];
}

function activityTypeThrows(state: State, peer: Peer, typeName: string): ActivityType {
    const at: ActivityType | undefined = activityState.activityTypes(state, peer.identity.user)?.[typeName];
    if (!at) {
        throwReason(activityMissingActivityType, `Unable to find activity type ${typeName}`);
    }
    return at;
}

function mergeConfig(activityPeer: ActivityPeer, configuration) {
    const baseConfiguration = activityPeer.configuration;
    // todo
}

type CreateArgs = Immutable<{ state: State, activity: Activity, messages: Array<SendMessageAction<MessageType>> }>;

function createOwner(ctx: CreateArgs, ownerType: ActivityPeer, creatorId: PeerId, context: Context, configuration): CreateArgs {
    const {
        state,
        messages,
        requestId
    } = createActivityPeer(ctx.state, creatorId, ownerType, ctx.activity, context, true, mergeConfig(ownerType, configuration));
    const result: CreateArgs = produce(ctx, (draft: Draft<CreateArgs>) => {
        draft.state = castDraft(state);
        draft.messages = draft.messages.concat(messages);
        draft.activity.gatewayRequests = draft.activity.gatewayRequests || new Set<GwRequestId>();
        draft.activity.gatewayRequests.add(requestId);
    });
    return result;
}

function createParticipants(ctx: CreateArgs, helperTypes: readonly ActivityPeer[] | undefined, creatorId: PeerId, context: Context, configuration): CreateArgs {
    if (helperTypes) {
        return helperTypes.reduce((xxx: CreateArgs, peerType: ActivityPeer) => {
            const {
                state,
                messages,
                requestId
            } = createActivityPeer(xxx.state, creatorId, peerType, xxx.activity, context, false, mergeConfig(peerType, configuration))
            const result: CreateArgs = produce(ctx, (draft: Draft<CreateArgs>) => {
                draft.state = castDraft(state);
                draft.messages = draft.messages.concat(messages);
                draft.activity.gatewayRequests = draft.activity.gatewayRequests || new Set<GwRequestId>();
                draft.activity.gatewayRequests.add(requestId);
            });
            return result;
        }, ctx);
    }
    return ctx;
}

export function createActivity(state: State, source: Source, request: Omit<CreateActivityRequest, 'configuration'>, cfg?: ReadonlyArray<ActivityPeer>): HandleReturnType {
    const {request_id: requestId, peer_id: peerId} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const activityTypeName = request.activity_type;
    const activityType: ActivityType = activityTypeThrows(state, peer, activityTypeName);
    //the configuration is now keyed by the activity peer's type and name
    const configuration: { [key: string]: object | null } = (cfg || [] as ActivityPeer[]).reduce((a, c) => {
        const key = `[${c.type},${c.name ?? ''}]`;
        a[key] = c.configuration ?? null;
        return a;
    }, {} as { [key: string]: object | null });
    const typesOverride = request.types_override;
    if (typesOverride && Object.keys(configuration).length !== 0) {
        throwReason(activityIsChild, `Cannot specify types override and custom configuration at the same time`);
    }

    let ctx: Context;
    let activity: Activity;
    [state, activity, ctx] = requestToActivity(state, request, peer);
    let messages: ReadonlyArray<SendMessageAction<MessageType>> = [activityInitiated(source, requestId, peerId, activity.id)];
    let args: CreateArgs = {state, messages, activity}
    args = createOwner(args, typesOverride?.owner_type ?? activityType.owner_type, peerId, ctx, configuration);
    const helperTypes: readonly ActivityPeer[] | undefined = typesOverride?.helper_types ?? (activityType.helper_types);
    args = createParticipants(args, helperTypes, peerId, ctx, configuration);

    ({state, activity, messages} = args);

    state = addContext(state, ctx);
    state = setActivity(state, activity.id, activity);


    return [state, messages];
}

/**
 * Sends a message to all peers in the activity domain that can see the activity and the activity participants
 */
function broadcastOwnerChanged(state: State, activity: Activity, owner: Peer): SendMessageAction<'owner-changed'>[] {
    if (activity["ready?"]) {
        // if the activity is ready (i.e. created message have been sent), we send a destroyed message
        // either participants or peers that can see the activity
        const ownerId = owner.id;
        return broadcastTargets(state, ownerId, activity, owner, true)
            .map(t => ownerChanged(t.source, t.id, activity.id, owner));
    }
    return []
}

/**
 * Processes a ready message from an activity owner. The activity becomes ready itself and messages for the
 currently ready members are broadcast. The activity created message is broadcast as well
 */
function ownerReady(state: State, activity: Activity, peer: Peer): { state: State, messages: Array<SendMessageAction<MessageType>>, activity: Activity } {
    const contextId = activity.contextId;
    const contextData = contextById(state, contextId)?.data || {};
    const ownerId = peer.id;
    const isReloading = peer[activityDomain]?.reload;
    activity = produce(activity, (draft: Draft<Activity>) => {
        draft.owner = ownerId;
        draft["ready?"] = true;
        delete draft.clientRequest;
    });
    let messages: SendMessageAction<MessageType>[] = [];
    if (isReloading) {
        messages = messages.concat(joinedActivity(state, peer.source, peer, activity, contextData));
        messages = messages.concat(broadcastOwnerChanged(state, activity, peer));
    } else {
        messages = messages.concat(broadcastJoinedActivity(state, activity, contextData));
        messages = messages.concat(broadcastActivityCreated(state, activity));
    }
    // returning a vector of the new state, the modified activity and the resulting messages
    return {
        state,
        // mark it as ready
        activity,

        messages
    }
}

/**
 * An activity can be seen by a peer in the activity domain that matches the read permissions. If there
 are no permissions set, the user of the peer must match the user of the activity owner
 */
function seesActivity(activity: Activity, owner: Peer, peer: Peer): boolean {
    const peerId = peer.id;
    if (activity.readyMembers?.has(peerId) || activity.participants?.has(peerId) || activity.owner === peerId) {
        // any activity members can actually see it
        return true;
    } else {
        // the rest are subject to permissions
        return isPeerVisibleIdentity([owner.source, owner.identity, activity.read_permissions, owner.options?.service], [peer.source, peer.identity, undefined, peer.options?.service]);
    }
}

/**
 * Broadcasts a message that a participant has joined an activity that has already started. The message is sent to
 all that can see the activity minus the participant itself (it should receive joined-activity)
 */
function broadcastActivityJoined(state: State, peer: Peer, activity: Activity, owner: Peer): SendMessageAction<'activity-joined'>[] {
    const peerId = peer.id;
    return broadcastTargets(state, peerId, activity, owner, true).map((p) => {
        return activityJoined(p.source, p.id, peer, activity.id);
    });
}

/**
 * Broadcasts a message that a participant has left the activity to everyone that can see it
 */
function broadcastActivityLeft(state: State, peerId: PeerId, activity: Activity, reason: Reason, includeParticipants: boolean): SendMessageAction<'left'>[] {
    const owner = byIdThrows(state, activity.owner);
    return broadcastTargets(state, peerId, activity, owner, includeParticipants).map((p) => {
        return activityLeft(p.source, p.id, peerId, activity.id, reason);
    });
}

/**
 * An activity can be destroyed by any of its members, or peers that can have write permissions for it.
 */
function canDestroy(state: State, activity: Activity, peer: Peer): boolean {
    const peerId = peer.id;

    if (activity.readyMembers?.has(peerId) || activity.participants?.has(peerId) || activity.owner === peerId) {
        // any activity member
        return true;
    } else {
        // the rest are subject to permissions
        const owner = byId(state, activity.owner);
        return isPeerVisibleIdentity([owner?.source, owner?.identity, activity.write_permissions, false], [peer.source, peer.identity, undefined, false]);
    }
}

/**
 * Sends a message to all participants in the activity that are ready plus the owner itself
 */
function broadcastJoinedActivity(state: State, activity: Activity, contextData): SendMessageAction<'joined'>[] {
    const ownerId = activity.owner;
    const readyMembers = activity.readyMembers;
    return (ownerId ? [ownerId] : []).concat(Array.from<PeerId>(readyMembers || []))
        .map(peerId => byId(state, peerId))
        .filter((peer): peer is Peer => !!peer)
        .map(peer => joinedActivity(state, peer.source, peer, activity, contextData));
}

function activityParticipants(state, activity: Activity, ownerId?: PeerId) {
    const result = new Set<PeerId>();
    if (ownerId) result.add(ownerId);
    if (activity.participants) activity.participants.forEach(result.add);
    if (activity.readyMembers) activity.readyMembers.forEach(result.add);
    return result;
}


function broadcastTargets(state, peerId, activity: Activity, owner: Peer, includeParticipants): Peer[] {
    const ids = new Set<PeerId>();
    activityState.activitySubscribers(state, activity.type).forEach(ids.add);
    if (includeParticipants) {
        activityParticipants(state, activity, owner?.id);
    }
    ids.delete(peerId);
    return Array.from(ids)
        .map((id) => byId(state, id)).filter((p): p is Peer => !!p)
        .filter((peer) => seesActivity(activity, owner, peer))
}

/**
 * Sends a message to all peers in the activity domain that are subscribed for that type of an activity
 */
function broadcastActivityCreated(state: State, activity: Activity): SendMessageAction<'created'>[] {
    const activityType = activity.type;
    const owner = byId(state, activity.owner)!;
    return Array.from(activityState.activitySubscribers(state, activityType))
        .map(p => byId(state, p)).filter((p): p is Peer => !!p)
        .filter(p => seesActivity(activity, owner, p))
        .map(p => activityCreated(state, p.source, p.id, activity));
}

/**
 * Processes a ready message from an activity participant. If the activity is ready, we need to broadcast events
 that we've joined the activity. Otherwise just add the peer to the set of ready members
 */
function participantReady(state: State, activity: Activity, peer: Peer): { state: State, messages: Array<SendMessageAction<MessageType>>, activity: Activity } {
    const peerId = peer.id;
    const contextId = activity.contextId;
    const context = contextState.contextById(state, contextId);
    activity = produce(activity, (draft: Draft<Activity>) => {
        draft.readyMembers = draft.readyMembers || new Set<PeerId>();
        draft.readyMembers.add(peerId);
    });
    let messages = new Array<SendMessageAction<MessageType>>();
    if (activity["ready?"]) {
        const ownerId = activity.owner;
        const owner = byIdThrows(state, ownerId);
        messages = messages.concat(broadcastActivityJoined(state, peer, activity, owner));
        messages = messages.concat(joinedActivity(state, peer.source, peer, activity, context?.data || {}));
    }
    return {state: contextState.addContextMember(state, context, peerId), activity, messages};
}

function cleanDomainInfo(peer: Peer): Peer {
    return produce(peer, (draft: Draft<Peer>) => {
        delete draft[activityDomain]?.member;
        delete draft[activityDomain]?.owner;
    });
}

/**
 * An activity member has sent a ready message
 */
export function peerReady(state: State, source, activityId, peer: Peer): HandleReturnType {
    const peerId = peer.id;
    let activity = activityState.activity(state, activityId);
    if (activity) {
        const isOwner = activity.owner === peerId;
        activity = produce(activity, (draft: Draft<Activity>) => {
            if (!isOwner) {
                draft.readyMembers = draft.readyMembers || new Set<PeerId>();
                draft.readyMembers.add(peerId);
            }
            draft.participants?.delete(peerId);
            if (draft.participants?.size === 0) {
                delete draft.participants;
            }
        });

        const {
            state: s,
            activity: a,
            messages: m
        } = isOwner ? ownerReady(state, activity, peer) : participantReady(state, activity, peer);
        return [activityState.setActivity(s, activityId, a), m];
    }
    // peer is ready for an activity that no longer exist - it should go away
    return [setPeer(state, peerId, cleanDomainInfo(peer)), [disposePeer(source, peerId, undefined, reasonActivityDestroyed)]];
}

function destroyParticipants(state: State, participantIds: PeerId[], reason: Reason): HandleReturnType {
    return participantIds.reduce(([s, m], pid) => {
        const participant = byId(s, pid);
        if (participant) {
            return [setPeer(s, pid, cleanDomainInfo(participant)), m.concat(disposePeer(participant.source, pid, undefined, reason))];
        }
        return [s, m];
    }, [state, [] as Array<SendMessageAction<'dispose-peer'>>]);
}

function removeGatewayRequests(state: State, activity: Activity): State {
    return (activity.gatewayRequests ? Array.from(activity.gatewayRequests) : []).reduce((s: State, requestId: GwRequestId) => {
        return removeGatewayRequest(s, requestId).state;
    }, state);
}

/**
 * Cleans up state associated with an activity
 */
function cleanupActivity(state: State, activity: Activity): State {
    state = removeGatewayRequests(state, activity);
    state = activityState.removeActivity(state, activity.id);
    state = contextState.removeContext(state, activity.contextId);
    return state;
}

/**
 * Dismantle the whole activity and send destroy messages to
 all participants that are already created. The outgoing and client requests are removed as well, so if a
 participant tries to join it receives an error
 */
function destroyActivity(state: State, activity: Activity, reason: Reason): HandleReturnType {
    const clientRequest = activity.clientRequest
    const ownerId = activity.owner;
    const activityId = activity.id;
    let messages: readonly SendMessageAction<MessageType | CommandType>[];
    [state, messages] = threadStateAndActions(state,
        s => [cleanupActivity(s, activity), []],
        (s) => {
            const participantIds = new Set<PeerId>();
            if (ownerId) participantIds.add(ownerId);
            if (activity.participants) activity.participants.forEach(participantIds.add);
            if (activity.readyMembers) activity.readyMembers.forEach(participantIds.add);
            return destroyParticipants(s, Array.from(participantIds), reason);
        });
    const requester = byId(state, clientRequest?.peer_id);
    if (requester) {
        return [state, messages.concat(error(requester.source, clientRequest?.request_id, clientRequest?.peer_id, reason))];
    }
    return [state, messages];
}

export function forwardDestroyActivity(state: State, source, request: DestroyRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, activity_id: activityId} = request;
    const peer: Peer = byIdThrows(state, peerId);
    const activity: Activity = activityThrows(state, activityId);
    if (canDestroy(state, activity, peer)) {
        return threadStateAndActions(state,
            s => destroyActivity(s, activity, requestToReason(request)),
            s => [s, [success(source, requestId, peerId)]]);
    }
    return [state, [error(source, requestId, peerId, reason(activityNotAuthorized, 'Not authorized to destroy activity'))]];
}

/**
 * The owner of the activity failed to create. Dismantle the whole activity and send destroy messages to
 all participants that are already created. The outgoing and client requests are removed as well, so if a
 participant tries to join it receives an error
 */
function ownerFailed(state: State, activityInfo: { id: string; "owner?": boolean }): HandleReturnType {
    // todo do we throw here?
    const activity = activityState.activityThrows(state, activityInfo.id)!;
    return destroyActivity(state, activity, reason(activityOwnerCreationFailed, 'Activity owner cannot be created'));
}

export function handleError(state: State, activityInfo: { id: string; "owner?": boolean }): HandleReturnType {
    if (activityInfo["owner?"]) {
        return ownerFailed(state, activityInfo);
    }
    return [state, []];
}

function joinParticipant(state: State, activity: Activity, participant: Peer): { state: State, activity: Activity, messages: ReadonlyArray<SendMessageAction<MessageType>> } {
    return {
        state: state,
        activity: activity,
        messages: []
    };
}

/**
 * Processes a request to join a target peer in an activity
 */
export function joinActivity(state: State, source: Source, request: JoinActivityRequest): HandleReturnType {
    const {
        request_id: requestId,
        peer_id: peerId,
        target_id: targetId,
        activity_id: activityId,
        peer_type: peerType,
        peer_name: peerName
    } = request;
    byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    const activity = activityThrows(state, activityId);
    const targetPeer: Peer = produce(byIdInDomainThrows(state, targetId, activityDomain), (draft: Draft<Peer>) => {
        if (peerType) {
            draft.peerType = peerType;
        }
        if (peerName) {
            draft.peerName = peerName;
        }
    });

    const memberId = targetPeer[activityDomain]?.member;
    const ownerId = targetPeer[activityDomain]?.owner;
    const existingActivity = activityState.activity(state, memberId ?? ownerId);


    if (existingActivity?.id === activityId) {
        // the peer is already in the same activity
        return [state, [success(source, requestId, peerId)]];
    } else if (existingActivity) {
        // in some other activity
        throwReason(activityIsChild, `Peer is already in activity ${existingActivity.id}`);
    }

    // joining a new activity
    const {state: nextState, messages} = joinParticipant(state, activity, targetPeer);
    return [nextState, messages.concat(success(source, requestId, peerId))];
}


/**
 * Removes a participant from the activity state
 */
function removeParticipant(activity: Activity, peerId: PeerId) {
    return produce(activity, (draft: Draft<Activity>) => {
        if (draft.participants) {
            draft.participants.delete(peerId);
            if (draft.participants.size === 0) {
                delete draft.participants;
            }
        }
        if (draft.readyMembers) {
            draft.readyMembers.delete(peerId);
            if (draft.readyMembers.size === 0) {
                delete draft.readyMembers;
            }
        }
    });
}

type ActivityStateAndMessages = {state: State, activity: Activity, messages: readonly SendMessageAction<MessageType>[]};

/**
 * A participant in an activity has left. Clean it up from the activity and its children
 */
function participantLeft(state: State, activity: Activity, peer: Peer, reason: Reason): ActivityStateAndMessages {
    if (log.enabledFor('debug')) {
        log.debug(`a participant ${peer.id} of activity ${activity.id} has left.`);
    }
    const peerId = peer.id;
    activity = removeParticipant(activity, peerId);
    const activityId = activity.id;
    const messages = broadcastActivityLeft(state, peerId, activity, reason, true);
    state = setPeer(state, peerId, cleanDomainInfo(peer));
    state = activityState.setActivity(state, activityId, activity);
    [state, ] = contextState.removeContextMember(state, contextState.contextById(state, activity.contextId)!, peerId);
    return {state, activity, messages};

}

/**
 * An owner of an activity has left. Dismantle the whole activity destroying all its children
 */
function ownerLeft(state: State, activity: Activity, peer: Peer, reason: Reason, shouldDestroy: boolean): HandleReturnType {
    const peerId = peer.id;
    if (log.enabledFor('debug')) {
        log.debug(`the owner ${peerId} of activity ${activity.id} has left`);
    }
    if (shouldDestroy) {
        return destroyActivity(setPeer(state, peerId, cleanDomainInfo(peer)), activity, reason);
    } else {
        const {state: nextState, messages} = participantLeft(state, activity, peer, reason);
        return [nextState, messages];
    }
}

/**
 * A peer is being removed from the domain, check if it belongs to any activities and deal with that accordingly
 */
export function removePeer(state: State, peer: Peer, reason: Reason): HandleReturnType {
    const activityId = peer[activityDomain]?.member;
    let activity = activityState.activity(state, activityId);
    if (activity) {
        const ownerId = activity.owner;
        const peerId = peer.id;
        const isReloading = activity.reloading && activity.reloading.has(peerId);
        activity = produce(activity, (draft: Draft<Activity>) => {
            if (isReloading) {
                if (draft.reloading) {
                    draft.reloading.delete(peerId);
                    if (draft.reloading.size === 0) {
                        delete draft.reloading;
                    }
                }
            }
        });
        if (ownerId === peerId) {
            return ownerLeft(state, activity, peer, reason, !isReloading);
        } else {
            const {state: s, messages} = participantLeft(state, activity, peer, reason);
            return [s, messages];
        }
    }
    return [state, []];
}

function subscribeUnsubscribe(state: State, source, requestId, peerId: PeerId, activityTypes: string[] | undefined, actionFn: (state: State, peerId: PeerId, activityType: true | string) => State): HandleReturnType {
    if (activityTypes) {
        state = activityTypes.filter((activityType): activityType is string => !!activityType).reduce((currentState: State, activityType: string) => {
            return actionFn(currentState, peerId, activityType);
        }, state);
    } else {
        state = actionFn(state, peerId, true);
    }
    return [state, [success(source, requestId, peerId)]];
}

export function subscribe(state: State, source: Source, request: SubscribeRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, activity_types: activityTypes} = request;
    const peer = byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    let messages: readonly SendMessageAction<MessageType | CommandType>[];
    [state, messages] = subscribeUnsubscribe(state, source, requestId, peerId, activityTypes, activityState.addActivitySubscriber);
    // activities that are ready and not children to other ones are announced
    const readyActivities = activityState.activities(state).filter((activity) => activity["ready?"]);

    messages = messages.concat(
        (activityTypes ? readyActivities.filter((a) => activityTypes.indexOf(a.type) !== -1) : readyActivities)
            .map((activity) => {
                return activityCreated(state, peer.source, peer.id, activity);
            }));

    return [state, messages];
}

export function unsubscribe(state: State, source, request: UnsubscribeRequest): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, activity_types: activityTypes} = request;
    byIdInDomainThrows(state, peerId, DomainKeys.Activity);
    return subscribeUnsubscribe(state, source, requestId, peerId, activityTypes, activityState.removeActivitySubscriber);
}
